import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Mail, Bell, Calendar, Check, X, Loader2, Clock, Users, Ruler, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

import { API } from '../config/api';

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function EmailReminderSettings() {
  const [settings, setSettings] = useState(null);
  const [athletes, setAthletes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingSend, setTestingSend] = useState(false);

  useEffect(() => {
    fetchSettings();
    fetchAthletesNeedingMeasurements();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/reminders/status`, { headers: getAuthHeaders() });
      setSettings(res.data);
    } catch (err) {
      console.error('Failed to fetch reminder settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchAthletesNeedingMeasurements = async () => {
    try {
      const res = await axios.get(`${API}/reminders/athletes-needing-measurements`, { 
        headers: getAuthHeaders() 
      });
      setAthletes(res.data);
    } catch (err) {
      console.error('Failed to fetch athletes');
    }
  };

  const toggleReminders = async (enabled) => {
    setSaving(true);
    try {
      await axios.post(
        `${API}/reminders/settings?enabled=${enabled}`,
        {},
        { headers: getAuthHeaders() }
      );
      setSettings(prev => ({ ...prev, enabled }));
      toast.success(enabled ? 'Reminders enabled' : 'Reminders disabled');
    } catch (err) {
      toast.error('Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const sendTestReminder = async () => {
    setTestingSend(true);
    try {
      const res = await axios.post(
        `${API}/reminders/send-testing-reminder`,
        {},
        { headers: getAuthHeaders() }
      );
      if (res.data.success) {
        toast.success('Test email sent! Check your inbox.');
      } else {
        toast.error('Failed to send test email');
      }
    } catch (err) {
      toast.error('Failed to send test email');
    } finally {
      setTestingSend(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 animate-pulse">
        <div className="h-6 bg-zinc-800 rounded w-48 mb-4" />
        <div className="h-20 bg-zinc-800 rounded" />
      </div>
    );
  }

  return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden" data-testid="email-reminder-settings">
      {/* Header */}
      <div className="p-4 border-b border-zinc-800 flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
          <Mail className="h-5 w-5 text-blue-500" />
        </div>
        <div>
          <h3 className="font-bold text-white">Email Reminders</h3>
          <p className="text-xs text-zinc-400">Automated progress & measurement reminders</p>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Email Status */}
        <div className={`flex items-center gap-3 p-3 rounded-lg ${
          settings?.email_available 
            ? 'bg-green-500/10 border border-green-500/30' 
            : 'bg-red-500/10 border border-red-500/30'
        }`}>
          {settings?.email_available ? (
            <>
              <Check className="h-5 w-5 text-green-500" />
              <span className="text-sm text-green-400">Email service connected</span>
            </>
          ) : (
            <>
              <X className="h-5 w-5 text-red-500" />
              <span className="text-sm text-red-400">Email service not configured</span>
            </>
          )}
        </div>

        {/* Reminder Types */}
        <div className="space-y-3">
          {/* 6-Week Testing Reminder */}
          <div className="bg-zinc-800/50 rounded-lg p-4">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3">
                <Calendar className="h-5 w-5 text-[#02b5ff] mt-0.5" />
                <div>
                  <h4 className="font-semibold text-white">6-Week Testing Reminder</h4>
                  <p className="text-xs text-zinc-400 mt-1">
                    Reminds you to book performance assessments every 6 weeks
                  </p>
                </div>
              </div>
              <Button
                size="sm"
                variant={settings?.enabled ? "default" : "outline"}
                onClick={() => toggleReminders(!settings?.enabled)}
                disabled={saving || !settings?.email_available}
                className={settings?.enabled ? "bg-[#02b5ff]" : "border-zinc-700"}
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : settings?.enabled ? (
                  <><Check className="h-4 w-4 mr-1" /> On</>
                ) : (
                  'Off'
                )}
              </Button>
            </div>
            
            {settings?.last_sent && (
              <div className="mt-3 pt-3 border-t border-zinc-700 flex items-center gap-2 text-xs text-zinc-500">
                <Clock className="h-3 w-3" />
                Last sent: {new Date(settings.last_sent).toLocaleDateString()}
              </div>
            )}
          </div>

          {/* Monthly Measurement Reminder */}
          <div className="bg-zinc-800/50 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Ruler className="h-5 w-5 text-[#ff025f] mt-0.5" />
              <div>
                <h4 className="font-semibold text-white">Monthly Measurement Reminder</h4>
                <p className="text-xs text-zinc-400 mt-1">
                  Monthly prompt to update height, weight, and other measurements for athletes under 21
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Athletes Needing Measurements */}
        {athletes && athletes.needing_update > 0 && (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-amber-500 mt-0.5" />
              <div>
                <h4 className="font-semibold text-amber-400">
                  {athletes.needing_update} athlete{athletes.needing_update > 1 ? 's' : ''} need measurement updates
                </h4>
                <div className="mt-2 space-y-1">
                  {athletes.athletes
                    .filter(a => a.needs_update)
                    .slice(0, 5)
                    .map((athlete, i) => (
                      <div key={i} className="flex items-center justify-between text-xs">
                        <span className="text-zinc-300">{athlete.name}</span>
                        <span className="text-zinc-500">
                          {athlete.last_measurement 
                            ? `Last: ${new Date(athlete.last_measurement).toLocaleDateString()}`
                            : 'Never measured'
                          }
                        </span>
                      </div>
                    ))
                  }
                  {athletes.needing_update > 5 && (
                    <p className="text-xs text-zinc-500">
                      +{athletes.needing_update - 5} more
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Test Email Button */}
        {settings?.email_available && (
          <Button
            onClick={sendTestReminder}
            disabled={testingSend}
            variant="outline"
            className="w-full border-zinc-700 hover:border-[#02b5ff]/50"
            data-testid="send-test-email-btn"
          >
            {testingSend ? (
              <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Sending...</>
            ) : (
              <><Mail className="h-4 w-4 mr-2" /> Send Test Email</>
            )}
          </Button>
        )}

        {/* Info */}
        <p className="text-xs text-zinc-600 text-center">
          Emails are sent via Resend to your registered email address
        </p>
      </div>
    </div>
  );
}
